import { CheckCircleFilled, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Flex, Skeleton, Space, Tag, Tooltip, Typography } from 'antd';
import type { CitacaoConferida, SituacaoDaCitacao } from '@titularis/api-client';
import { api } from '../api';

/**
 * As citações do comentário, conferidas contra o acervo (G-VERIFY, G-PROVENANCE).
 *
 * **É o portão da escrita, mostrado a quem revisa.** A gravação já recusa a
 * citação que a fonte desmente, mas isso acontece antes da revisão: quem abre a
 * questão para conferir não vê nada disso e acaba fazendo à mão o que a máquina
 * já fez - abrir o Planalto e comparar palavra por palavra, que é justamente o
 * passo que o módulo existe para eliminar.
 *
 * **O silêncio é o resultado bom.** Citação que confere não vira linha: uma
 * lista de vinte e cinco itens verdes esconderia os três que pedem atenção. O
 * que aparece por extenso é o que tem algo a resolver, e o resto vira contagem.
 */

interface Aparencia {
  rotulo: string;
  cor: string;
  explicacao: string;
  /** Se pede ação de quem revisa. O que não pede vira contagem. */
  pedeAtencao: boolean;
}

const SITUACAO: Record<SituacaoDaCitacao, Aparencia> = {
  confere: {
    rotulo: 'Confere',
    cor: 'green',
    explicacao: 'A transcrição bate com a fonte, caractere a caractere.',
    pedeAtencao: false,
  },
  sem_transcricao: {
    rotulo: 'Sem transcrição',
    cor: 'default',
    explicacao:
      'A citação aponta o dispositivo mas não transcreve o texto. Não há o que conferir - e a referência existe no acervo.',
    pedeAtencao: false,
  },
  lei_ausente: {
    rotulo: 'Fora do acervo',
    cor: 'default',
    explicacao:
      'A lei citada ainda não foi importada. É lacuna do acervo, não erro de quem escreveu: conferir esta citação continua manual.',
    pedeAtencao: true,
  },
  dispositivo_ausente: {
    rotulo: 'Dispositivo inexistente',
    cor: 'red',
    explicacao:
      'A lei está no acervo, mas não tem este endereço. É o erro que o módulo existe para pegar - "art. 26-A" onde a lei diz "art. 26-B".',
    pedeAtencao: true,
  },
  transcricao_cortada: {
    rotulo: 'Transcrição cortada',
    cor: 'orange',
    explicacao:
      'O texto bate, mas tem "[...]" no meio. O estilo da casa pede transcrição integral: não corte pedaços da lei citada.',
    pedeAtencao: true,
  },
  diverge: {
    rotulo: 'Diverge da fonte',
    cor: 'red',
    explicacao: 'A fonte diz outra coisa. Compare abaixo e corrija a transcrição.',
    pedeAtencao: true,
  },
};

function Linha({ citacao }: { citacao: CitacaoConferida }) {
  const aparencia = SITUACAO[citacao.situacao];

  return (
    <Space orientation="vertical" size={2} style={{ width: '100%' }}>
      <Space size={6} wrap>
        <Tag color={aparencia.cor} style={{ marginInlineEnd: 0 }}>
          {aparencia.rotulo}
        </Tag>
        {/*
          `wordBreak` porque a referência é longa e não tem espaço onde quebrar:
          "Art. 26-B, caput, § 1º, inciso I, da Lei 8.629/1993" num cartão
          estreito empurraria o painel inteiro para fora da tela.
        */}
        <Typography.Text strong style={{ wordBreak: 'break-word' }}>
          {citacao.referencia}
        </Typography.Text>
      </Space>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {aparencia.explicacao}
      </Typography.Text>

      {/*
        A proveniência por citação (G-PROVENANCE): qual lei, e onde conferir. Sem
        o link, a tela diria "diverge" e ainda obrigaria a procurar a fonte.
      */}
      {citacao.titulo ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {citacao.titulo}
          {citacao.alteradoPor ? ` · ${citacao.alteradoPor}` : ''}
          {citacao.fonteOficial ? (
            <>
              {' · '}
              <Typography.Link href={citacao.fonteOficial} target="_blank" rel="noreferrer">
                fonte oficial
              </Typography.Link>
            </>
          ) : null}
        </Typography.Text>
      ) : null}

      {/*
        Os dois textos lado a lado, e não só o aviso: dizer "diverge" sem mostrar
        onde devolve o revisor ao trabalho manual que o verificador substituiu.
      */}
      {citacao.situacao === 'diverge' && citacao.naFonte ? (
        <Space orientation="vertical" size={2} style={{ width: '100%', marginTop: 4 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            No comentário:
          </Typography.Text>
          <Typography.Paragraph
            type="danger"
            style={{ fontSize: 12, marginBottom: 4 }}
            ellipsis={{ rows: 3, expandable: true, symbol: 'ver tudo' }}
          >
            {citacao.transcrito}
          </Typography.Paragraph>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Na fonte:
          </Typography.Text>
          <Typography.Paragraph
            style={{ fontSize: 12, marginBottom: 0 }}
            ellipsis={{ rows: 3, expandable: true, symbol: 'ver tudo' }}
          >
            {citacao.naFonte}
          </Typography.Paragraph>
        </Space>
      ) : null}
    </Space>
  );
}

/**
 * O acervo que sustentou a sugestão, com o nível de cada peça (D-23).
 *
 * **O Nível 2 aparece marcado, e é o ponto.** `registro` é citável por número e
 * verificável; `texto` é parafraseável e nunca citável - ele entrou no acervo
 * sem um parser que o estruturasse, então o conteúdo vale e a numeração não.
 * Sem a marca, quem revisa trata os dois igual, que é exatamente o que o
 * G-CORPUS-TIER existe para impedir.
 */
function Acervo({
  pecas,
  lacunas,
}: {
  pecas: { tier: string; identity: string | null; origem: string; porque: string }[];
  lacunas: string[];
}) {
  if (pecas.length === 0 && lacunas.length === 0) return null;

  const naoCitaveis = pecas.filter((peca) => peca.tier === 'texto');

  return (
    <Space orientation="vertical" size={4} style={{ width: '100%' }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Escrito com {pecas.length} {pecas.length === 1 ? 'peça' : 'peças'} do acervo.
      </Typography.Text>

      {naoCitaveis.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          title={`${naoCitaveis.length} ${naoCitaveis.length === 1 ? 'peça é de Nível 2' : 'peças são de Nível 2'}`}
          description="Material sem estrutura reconhecida: o conteúdo vale, mas não pode ser citado por número. Confira se o comentário parafraseia em vez de citar."
        />
      ) : null}

      {naoCitaveis.map((peca, indice) => (
        <Typography.Text key={indice} type="secondary" style={{ fontSize: 12 }}>
          <Tag color="orange" style={{ marginInlineEnd: 6 }}>
            Nível 2
          </Tag>
          {peca.origem}
        </Typography.Text>
      ))}

      {/*
        A lacuna declarada: o que a questão cita e o acervo não tem. Dizer o que
        falta é mais seguro do que omitir - o silêncio convida a preencher de
        memória, que é o defeito de origem do módulo.
      */}
      {lacunas.map((lacuna, indice) => (
        <Typography.Text key={indice} type="warning" style={{ fontSize: 12 }}>
          Não estava no acervo: {lacuna}
        </Typography.Text>
      ))}
    </Space>
  );
}

/**
 * O que o gabarito oficial diz sobre esta questão, e como foi lido.
 *
 * **É o passo 5 do P-A-P deixando de ser necessário.** O Róger abre o PDF da
 * prova e o do gabarito para conferir se a resposta bate, e o faz por
 * desconfiança - a pipeline já leu a grade e sabe a letra. Mostrar aqui
 * transforma "abrir dois PDFs" em "bater o olho".
 *
 * **Como foi lido é o que decide quanto conferir.** O parser determinístico
 * acerta as três provas do golden set por inteiro; o modelo acertou de 87% a
 * 99%, variando por execução. Sem o campo, as duas leituras pareceriam iguais.
 */
function Gabarito({
  gabarito,
}: {
  gabarito: {
    bookletType: string | null;
    letra: string | null;
    anulada: boolean;
    lidoPor: string | null;
    fileira: { startPosition: number; marks: string } | null;
  };
}) {
  const lidoPeloParser = gabarito.lidoPor === 'parser-deterministico';

  return (
    <Space orientation="vertical" size={2} style={{ width: '100%' }}>
      <Space size={6} wrap>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Gabarito oficial:
        </Typography.Text>
        {gabarito.anulada ? (
          <Tag color="orange" style={{ marginInlineEnd: 0 }}>
            Anulada
          </Tag>
        ) : (
          <Tag color="blue" style={{ marginInlineEnd: 0 }}>
            {(gabarito.letra ?? '?').toUpperCase()}
          </Tag>
        )}
        {gabarito.bookletType ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            caderno {gabarito.bookletType}
          </Typography.Text>
        ) : null}
      </Space>

      {/*
        A fileira como impressa: o número onde ela começa e a sequência de
        letras, do jeito que está no papel. É o que o revisor veria abrindo o
        PDF, e é por isso que ele deixa de precisar abrir.
      */}
      {gabarito.fileira ? (
        // A sequência de letras não tem espaço nenhum: sem quebrar em qualquer
        // ponto, uma fileira de quarenta marcas estoura a largura do cartão.
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}
        >
          fileira a partir de {gabarito.fileira.startPosition}: {gabarito.fileira.marks}
        </Typography.Text>
      ) : null}

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {lidoPeloParser
          ? 'Lido pelo parser determinístico, que acerta o golden set por inteiro.'
          : `Lido por ${gabarito.lidoPor ?? 'modelo'} - a leitura por modelo varia entre execuções, e vale conferir.`}
      </Typography.Text>
    </Space>
  );
}

export function CitacoesConferidas({ questionId }: { questionId: string }) {
  const conferencia = useQuery({
    queryKey: ['citacoes', questionId],
    queryFn: () => api.conferirCitacoes(questionId),
  });

  if (conferencia.isPending) return <Skeleton active paragraph={{ rows: 2 }} />;

  if (conferencia.error) {
    return <Alert type="error" showIcon title={(conferencia.error as Error).message} />;
  }

  const vereditos = conferencia.data?.vereditos ?? [];
  const pecas = conferencia.data?.pecas ?? [];
  const lacunas = conferencia.data?.lacunas ?? [];

  /*
   * Sem citação **e** sem acervo não há o que dizer. Com acervo e sem citação há:
   * o comentário pode ter sido escrito só com jurisprudência, e o nível dela
   * continua importando.
   */
  if (
    vereditos.length === 0 &&
    pecas.length === 0 &&
    lacunas.length === 0 &&
    !conferencia.data?.gabarito
  ) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Nenhuma citação de lei no comentário."
      />
    );
  }

  const atencao = vereditos.filter((v) => SITUACAO[v.situacao].pedeAtencao);
  const conferem = vereditos.filter((v) => v.situacao === 'confere').length;
  const semTranscricao = vereditos.filter((v) => v.situacao === 'sem_transcricao').length;

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Flex justify="space-between" align="flex-start" gap={8}>
        {atencao.length === 0 ? (
          <Space size={6}>
            <CheckCircleFilled style={{ color: 'var(--ant-color-success)' }} />
            <Typography.Text>
              {vereditos.length} {vereditos.length === 1 ? 'citação conferida' : 'citações conferidas'}
              , nenhuma pendência.
            </Typography.Text>
          </Space>
        ) : (
          <Typography.Text>
            <Typography.Text strong>{atencao.length}</Typography.Text> de {vereditos.length}{' '}
            {vereditos.length === 1 ? 'citação pede' : 'citações pedem'} atenção.
          </Typography.Text>
        )}
        <Tooltip title="Conferir de novo contra o acervo">
          <Button
            size="small"
            type="text"
            icon={<ReloadOutlined />}
            loading={conferencia.isFetching}
            onClick={() => void conferencia.refetch()}
            aria-label="Conferir as citações de novo"
          />
        </Tooltip>
      </Flex>

      {atencao.map((citacao, indice) => (
        <Linha key={`${citacao.referencia}-${indice}`} citacao={citacao} />
      ))}

      {conferencia.data?.gabarito ? <Gabarito gabarito={conferencia.data.gabarito} /> : null}

      <Acervo pecas={pecas} lacunas={lacunas} />

      {/*
        O que está bem vira contagem, e não lista. Vinte e cinco linhas verdes
        esconderiam as três que pedem ação - e o painel existe para as três.
      */}
      {conferem + semTranscricao > 0 ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {conferem > 0 ? `${conferem} confere${conferem > 1 ? 'm' : ''} com a fonte` : ''}
          {conferem > 0 && semTranscricao > 0 ? ' · ' : ''}
          {semTranscricao > 0
            ? `${semTranscricao} ${semTranscricao > 1 ? 'citam' : 'cita'} sem transcrever`
            : ''}
          .
        </Typography.Text>
      ) : null}
    </Space>
  );
}
